import * as React from 'react'
import Input from '@frontrow/console/src/entities/components/base/input'
import {Label} from '@frontrow/console/src/components/ui/label'
import {Alert} from '@frontrow/console/src/components/ui/alert'
import {Card} from '@frontrow/console/src/components/ui/card'
import {H2} from '@frontrow/console/src/components/typography'
import {fromMaybe} from '@freckle/maybe'
import {type StarStatus} from '@frontrow/console/src/entities/models/school'
import {FIELD_GRID} from '@frontrow/console/src/lib/form-styles'

import FormField from '@frontrow/console/src/components/common/form-field'
import {RenaissanceIDPError} from '@frontrow/console/src/components/common/renaissance-idp-error'
import LabelledInput from '@frontrow/console/src/components/common/labelled-input'

type Props = {
  renaissanceFieldsRequired: boolean
  renaissanceSchoolClientClientId: string | undefined | null
  renaissanceSchoolClientRpid: string | undefined | null
  renaissanceSchoolClientBaseUrl: string | undefined | null
  renaissanceSchoolClientPlatform: string
  renaissanceSchoolClientStarStatus: StarStatus
  renaissanceRpIdentifier?: string | null
  showStarSyncFreshlyEnabledNotification: boolean
  needsStarIntegrationFields: boolean
  showRenaissanceIDPError: boolean
  disabled?: boolean
  onChangeBaseUrl: (baseUrl: string) => void
  onChangeClientId: (clientId: string) => void
  onChangeRenaissancePlatform: (e: React.ChangeEvent<HTMLInputElement>) => void
  onChangeRpIdentifier?: (rpIdentifier: string) => void
  onChangeRpid: (rpid: string) => void
  onUpdateStarStatus: (starStatus: StarStatus) => void
}

// Half a row on a wide window, the full row below `lg` — because half of a
// narrow window is not enough for a 36-character UUID, and a pair of fields
// should break to full width together.
const HALF_WIDE = 'mb-0 pr-0 sm:col-span-12 lg:col-span-6'

export function RenaissanceConfigurationFormFields(props: Props): React.ReactElement {
  const {
    renaissanceFieldsRequired,
    renaissanceSchoolClientClientId,
    onChangeClientId,
    renaissanceSchoolClientRpid,
    onChangeRpid,
    renaissanceSchoolClientBaseUrl,
    onChangeBaseUrl,
    renaissanceRpIdentifier,
    onChangeRpIdentifier,
    renaissanceSchoolClientStarStatus,
    onUpdateStarStatus,
    showStarSyncFreshlyEnabledNotification,
    renaissanceSchoolClientPlatform,
    onChangeRenaissancePlatform,
    needsStarIntegrationFields,
    showRenaissanceIDPError,
    disabled
  } = props

  const disableField = fromMaybe(() => false, disabled)

  const starIntegrationError = needsStarIntegrationFields ? (
    <React.Fragment>
      The following Renaissance Configuration information is required to enable STAR integration:{' '}
      <strong>RPID</strong> and <strong>Base URL</strong>.
    </React.Fragment>
  ) : null

  const renaissanceMisconfigurationNotification =
    needsStarIntegrationFields && showRenaissanceIDPError ? (
      <div className="mb-4">
        <Alert type="danger">
          {starIntegrationError}
          <br />
          <br />
          <RenaissanceIDPError />
        </Alert>
      </div>
    ) : null

  const enableStarNotification = showStarSyncFreshlyEnabledNotification ? (
    <div className="mt-3">
      <Alert type="warning">
        Enabling Star Integration will trigger confirmation emails to administrators about how to
        complete the integration.
      </Alert>
    </div>
  ) : null

  const starMisconfigurationNotification =
    needsStarIntegrationFields && !showRenaissanceIDPError ? (
      <div className="mb-3">
        <Alert type="danger">{starIntegrationError}</Alert>
      </div>
    ) : null

  return (
    <Card htmlElement="section" id="renaissance-configuration">
      <div className="mb-4">
        <H2 addClass="my-0">Renaissance Configuration</H2>
      </div>
      {renaissanceMisconfigurationNotification}

      <div className={FIELD_GRID}>
        <FormField
          wrapperStyle={HALF_WIDE}
          label="Client Id"
          htmlFor="renaissance-client-id"
          required={renaissanceFieldsRequired}
        >
          <Input
            id="renaissance-client-id"
            className="font-mono"
            dataTest="renaissance-client-id"
            value={renaissanceSchoolClientClientId || ''}
            type="text"
            onChange={onChangeClientId}
            disabled={disableField}
          />
        </FormField>
        <FormField
          wrapperStyle={HALF_WIDE}
          label="RPID"
          htmlFor="renaissance-rpid"
          required={renaissanceFieldsRequired}
        >
          <Input
            id="renaissance-rpid"
            className="font-mono"
            dataTest="renaissance-rpid"
            value={renaissanceSchoolClientRpid || ''}
            type="text"
            onChange={onChangeRpid}
            disabled={disableField}
          />
        </FormField>

        <FormField
          wrapperStyle={HALF_WIDE}
          label="Base URL (starting with https)"
          htmlFor="renaissance-base-url"
          required={renaissanceFieldsRequired}
        >
          <Input
            id="renaissance-base-url"
            className="font-mono"
            dataTest="renaissance-base-url"
            value={renaissanceSchoolClientBaseUrl || ''}
            type="text"
            onChange={onChangeBaseUrl}
            disabled={disableField}
          />
        </FormField>
        {onChangeRpIdentifier !== null && onChangeRpIdentifier !== undefined ? (
          <FormField
            wrapperStyle={HALF_WIDE}
            label="RP Identifier"
            htmlFor="renaissance-rp-identifier"
          >
            <Input
              id="renaissance-rp-identifier"
              className="font-mono"
              value={renaissanceRpIdentifier || ''}
              type="text"
              onChange={onChangeRpIdentifier}
              disabled={disableField}
            />
          </FormField>
        ) : null}

        <hr className="border-border sm:col-span-12" />

        <div className="sm:col-span-4">
          <Label>Renaissance Platform</Label>
          <div className="mt-2 flex flex-col gap-1.5">
            <LabelledInput label="RDC">
              <input
                data-test="renaissance-place"
                type="radio"
                value="renaissance_place"
                checked={renaissanceSchoolClientPlatform === 'renaissance_place'}
                onChange={onChangeRenaissancePlatform}
                disabled={disableField}
              />
            </LabelledInput>
            <LabelledInput label="RGP">
              <input
                data-test="renaissance-growth-platform"
                type="radio"
                value="renaissance_growth_platform"
                checked={renaissanceSchoolClientPlatform === 'renaissance_growth_platform'}
                onChange={onChangeRenaissancePlatform}
                disabled={disableField}
              />
            </LabelledInput>
          </div>
        </div>

        <div className="sm:col-span-8">
          <Label>Star Integration</Label>
          <p className="mb-3 mt-1 text-xs text-muted-text">
            When this is enabled, Star Math and/or Star Reading results will sync into Freckle,
            depending on which licenses are currently active (Math or ELA).
          </p>

          {starMisconfigurationNotification}
          <div className="flex flex-col gap-1.5">
            <LabelledInput label="On" comment="Star is enabled">
              <input
                type="radio"
                checked={renaissanceSchoolClientStarStatus === 'star_enabled'}
                onChange={() => onUpdateStarStatus('star_enabled')}
                disabled={disableField}
                data-test="renaissance-star-integration-on"
              />
            </LabelledInput>
            <LabelledInput
              label="Off"
              comment="Star will turn on automatically if the customer rosters via RGP in the future"
            >
              <input
                type="radio"
                checked={renaissanceSchoolClientStarStatus === 'star_unset'}
                onChange={() => onUpdateStarStatus('star_unset')}
                disabled={disableField}
                data-test="renaissance-star-integration-off"
              />
            </LabelledInput>
            <LabelledInput label="Disabled" comment="this customer does NOT want Star integration">
              <input
                type="radio"
                checked={renaissanceSchoolClientStarStatus === 'star_disabled'}
                onChange={() => onUpdateStarStatus('star_disabled')}
                disabled={disableField}
                data-test="renaissance-star-integration-disabled"
              />
            </LabelledInput>
          </div>

          {enableStarNotification}
        </div>
      </div>
    </Card>
  )
}
